import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  Chip,
  Button,
} from '@mui/material';
import { PaymentFrequency } from '../../__generated__/types';

type PlaceholderTier = 'Home' | 'Design' | 'Design+' | 'Basic' | 'Guild' | 'Guild+';

interface PlaceholderCardProps {
  tier: PlaceholderTier;
  frequency: PaymentFrequency;
  isComingSoon?: boolean;
  onAddPlan?: (tier: string, frequency: PaymentFrequency) => void;
}

export const PlaceholderCard: React.FC<PlaceholderCardProps> = ({
  tier,
  frequency,
  isComingSoon = false,
  onAddPlan,
}) => {
  const getFrequencyLabel = (freq: PaymentFrequency) => {
    switch (freq) {
      case PaymentFrequency.Monthly:
        return 'Monthly';
      case PaymentFrequency.Annually:
        return 'Annual';
      case PaymentFrequency.BiAnnually:
        return 'Bi-Annual';
      default:
        return 'Monthly';
    }
  };

  const getTierColor = (tierName: PlaceholderTier) => {
    switch (tierName) {
      case 'Home':
        return '#4CAF50'; // Green
      case 'Design':
        return '#2196F3'; // Blue
      case 'Design+':
        return '#9C27B0'; // Purple
      case 'Basic':
        return '#6B7280'; // Slate
      case 'Guild':
        return '#2563EB'; // Indigo
      case 'Guild+':
        return '#D22268'; // Brand pink
      default:
        return '#757575'; // Grey
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '2px dashed #e0e0e0',
        backgroundColor: '#fafafa',
        opacity: 0.7,
        transition: 'all 0.3s ease',
        '&:hover': {
          opacity: 1,
          borderColor: getTierColor(tier),
          backgroundColor: '#f5f5f5',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
        {/* Tier Badge */}
        <Chip
          label={tier}
          sx={{
            backgroundColor: getTierColor(tier),
            color: 'white',
            fontWeight: 600,
            py: 0.5, // 8px
            px: 0.5, // 8px
            minWidth: 'auto',
            width: 'fit-content',
            borderRadius: 50,
            mb: 2,
          }}
        />

        {/* Plan Title */}
        <Typography variant="h6" component="h3" gutterBottom>
          {tier} {getFrequencyLabel(frequency)}
        </Typography>

        {/* Status */}
        {isComingSoon ? (
          <Box sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Coming Soon
            </Typography>
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
          </Box>
        ) : (
          <Box sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Plan Not Configured
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add this plan to display pricing and features
            </Typography>
          </Box>
        )}

        {/* Features Placeholder */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Skeleton variant="text" width="80%" sx={{ mx: 'auto', mb: 1 }} />
          <Skeleton variant="text" width="70%" sx={{ mx: 'auto', mb: 1 }} />
          <Skeleton variant="text" width="75%" sx={{ mx: 'auto' }} />
        </Box>

        {/* Action Button */}
        {onAddPlan && !isComingSoon && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => onAddPlan(tier, frequency)}
            sx={{
              mt: 2,
              borderColor: getTierColor(tier),
              color: getTierColor(tier),
              '&:hover': {
                backgroundColor: getTierColor(tier),
                color: 'white',
              },
            }}
          >
            Configure Plan
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
